import { useMemo, useRef, useState } from "react";
import dayjs from "dayjs";

// Gantt-style lane timeline: several related boolean device states share ONE
// time axis, each on its own horizontal lane. A filled coloured bar marks the
// spans where that state is "on", so the interplay between related signals
// (e.g. defrost on → compressor + fan off) is readable at a glance without the
// overlap you'd get from stacking on/off lines on a single 2-level chart.
// Samples with no data (null/""/"-") leave the lane blank for that span.
//
// Hovering anywhere on the timeline drops a cursor line and shows a summary
// tooltip: the caller's `explain(row)` turns the full device state at that
// moment into plain-language reasons ("compressor off — defrost in progress").

interface LaneSeries {
  dataKey: string; // boolean row field
  name: string; // lane label (already translated)
  color: string;
  onLabel: string; // tooltip word for the "on" state
}
// one summary line in the hover tooltip: a colour dot (matched to the relevant
// lane so the eye can link reason ↔ timeline) + the plain-language reason.
export interface ReasonLine {
  color: string;
  text: string;
}
interface Props {
  data: any[];
  series: LaneSeries[];
  timeKey?: string; // default "time"
  labelWidth?: number;
  // full device row (all fields, not just this chart's lanes) → reason lines
  explain?: (row: any) => ReasonLine[];
  // Force the time axis to a fixed window instead of the data's own min/max.
  // Used so an event-transition dataset (which may not span the whole report
  // window) still renders on the full window axis, with the final held state
  // extended to domainEnd.
  domainStart?: any;
  domainEnd?: any;
}

const LANE_H = 34;

const toMs = (v: any): number =>
  typeof v === "number" ? v : dayjs(v).valueOf();
const isOn = (v: any): boolean | null =>
  v === null || v === undefined || v === "" || v === "-" ? null : !!v;

export default function ChartStateLanes({
  data,
  series,
  timeKey = "time",
  labelWidth = 118,
  explain,
  domainStart,
  domainEnd,
}: Props) {
  const rows = useMemo(
    () =>
      (data ?? [])
        .map((r) => ({ t: toMs(r?.[timeKey]), raw: r }))
        .sort((a, b) => a.t - b.t),
    [data, timeKey]
  );
  const t0 =
    domainStart != null ? toMs(domainStart) : rows.length ? rows[0].t : 0;
  const t1 =
    domainEnd != null
      ? toMs(domainEnd)
      : rows.length
      ? rows[rows.length - 1].t
      : 1;
  const span = Math.max(1, t1 - t0);
  const multiDay = span > 24 * 60 * 60 * 1000;
  const fmt = (t: number) =>
    multiDay ? dayjs(t).format("DD/MM HH:mm") : dayjs(t).format("HH:mm");

  // Contiguous "on" runs per lane → colored bars. A run closes at the first
  // non-on sample's time (the transition), so the bar ends where the device
  // switched off; an unbroken run to the end closes at the last sample.
  const lanes = useMemo(
    () =>
      series.map((s) => {
        const segs: { start: number; end: number }[] = [];
        let start: number | null = null;
        for (let i = 0; i < rows.length; i++) {
          const on = isOn(rows[i].raw?.[s.dataKey]);
          if (on === true && start === null) start = rows[i].t;
          if (on !== true && start !== null) {
            segs.push({ start, end: rows[i].t });
            start = null;
          }
        }
        // trailing "on" run holds until the end of the window (t1), not just
        // the last sample — matters when the last event is before window end
        if (start !== null) segs.push({ start, end: t1 });
        return { ...s, segs };
      }),
    [rows, series, t1]
  );

  const pct = (t: number) => ((t - t0) / span) * 100;

  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{
    px: number; // cursor x within the wrapper
    tMs: number;
    reasons: ReasonLine[];
  } | null>(null);

  const onMove = (e: React.MouseEvent) => {
    const el = wrapRef.current;
    if (!el || rows.length === 0) return;
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const trackW = rect.width - labelWidth;
    if (px < labelWidth || trackW <= 0) {
      setHover(null);
      return;
    }
    const frac = Math.min(1, Math.max(0, (px - labelWidth) / trackW));
    const tMs = t0 + frac * span;
    // step semantics: the state in effect at tMs = latest row with t <= tMs.
    // Stays null when tMs is before the first sample (event mode has no data
    // before the first transition) → tooltip shows "no data", matching the
    // blank bar there instead of borrowing the first row's state.
    let cur: any = null;
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].t <= tMs) cur = rows[i];
      else break;
    }
    const reasons = explain ? explain(cur?.raw) : [];
    setHover({ px, tMs, reasons });
  };

  const lanesHeight = series.length * LANE_H;

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative" }}
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
    >
      {lanes.map((lane) => (
        <div
          key={lane.dataKey}
          style={{ display: "flex", alignItems: "center", height: LANE_H }}
        >
          <div
            style={{
              width: labelWidth,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
              color: "#334155",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 9,
                height: 9,
                borderRadius: 9,
                background: lane.color,
                flexShrink: 0,
              }}
            />
            {lane.name}
          </div>
          <div
            style={{
              position: "relative",
              flex: 1,
              height: 18,
              background: "#f1f5f9",
              borderRadius: 4,
            }}
          >
            {lane.segs.map((sg, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: `${pct(sg.start)}%`,
                  width: `${Math.max(0.35, pct(sg.end) - pct(sg.start))}%`,
                  top: 0,
                  bottom: 0,
                  background: lane.color,
                  borderRadius: 2,
                }}
              />
            ))}
          </div>
        </div>
      ))}
      {/* shared time axis */}
      <div style={{ display: "flex", marginTop: 4 }}>
        <div style={{ width: labelWidth, flexShrink: 0 }} />
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11,
            color: "#94a3b8",
          }}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <span key={f}>{fmt(t0 + span * f)}</span>
          ))}
        </div>
      </div>

      {/* hover cursor line + summary tooltip */}
      {hover ? (
        <>
          <div
            style={{
              position: "absolute",
              left: hover.px,
              top: 0,
              height: lanesHeight,
              width: 1,
              background: "#94a3b8",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: Math.min(
                hover.px + 14,
                Math.max(0, (wrapRef.current?.clientWidth ?? 0) - 312)
              ),
              top: 0,
              width: 300,
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              boxShadow: "0 8px 24px rgba(15,23,42,0.14)",
              padding: "12px 14px",
              fontSize: 13,
              color: "#334155",
              pointerEvents: "none",
              zIndex: 30,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#1f2328",
                paddingBottom: 8,
                marginBottom: 8,
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              {fmt(hover.tMs)}
            </div>
            {hover.reasons.length ? (
              hover.reasons.map((r, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    lineHeight: 1.45,
                    marginBottom: i === hover.reasons.length - 1 ? 0 : 7,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: 8,
                      background: r.color,
                      marginTop: 5,
                      flexShrink: 0,
                    }}
                  />
                  <span>{r.text}</span>
                </div>
              ))
            ) : (
              <div style={{ color: "#94a3b8" }}>—</div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
